/**
 * GLAMRDiP Instant Gels BundleBuilder
 * - Count-driven: user picks N designs, tier auto-selects (highest tier where
 *   design_count <= N).
 * - Max selections = highest tier's design_count.
 * - Category chips filter the visible tiles.
 * - Single /cart/add.js items[] POST with:
 *     tier variant: { properties: { Designs: "A,B,C", __bundle_id } }
 *     each design:  { properties: { __bundle_id, _hidden: "true" } }
 * - Analytics: instant_gels_builder_open, _design_toggle, _tier_changed,
 *   _atc_click, _atc_success, _atc_error
 */
(() => {
  const roots = document.querySelectorAll('[data-gd-instant]');
  if (!roots.length) return;
  roots.forEach(hydrate);

  function hydrate(root) {
    const modal = root.querySelector('[data-gd-instant-modal]');
    if (!modal) return;
    const localOpen = root.querySelector('[data-gd-instant-open]');
    // PDP "Build your bundle" CTA uses data-gd-open-bundle-builder. That trigger
    // also exists on the Salon Kit hero, but kit-builder.liquid is not rendered
    // on the Instant Gels PDP template, so only one handler fires per page.
    const globalOpens = document.querySelectorAll('[data-gd-open-bundle-builder]');
    const closeBtns = root.querySelectorAll('[data-gd-instant-close]');
    const tiers = [...root.querySelectorAll('[data-gd-instant-tier]')]
      .map(el => ({
        el,
        variantId: parseInt(el.dataset.variantId, 10),
        count: parseInt(el.dataset.designCount, 10),
        price: parseInt(el.dataset.price, 10),
        compare: parseInt(el.dataset.compare, 10) || null,
        title: el.dataset.title,
      }))
      .sort((a, b) => a.count - b.count);

    const maxSelections = tiers.length ? tiers[tiers.length - 1].count : 0;
    const minSelections = tiers.length ? tiers[0].count : 0;

    const catChips = [...root.querySelectorAll('[data-gd-instant-cat]')];
    const designTiles = [...root.querySelectorAll('[data-gd-instant-design]')];

    const slotsHost = root.querySelector('[data-gd-instant-slots]');
    const statusEl = root.querySelector('[data-gd-instant-status]');
    const priceEl = root.querySelector('[data-gd-instant-price]');
    const wasEl = root.querySelector('[data-gd-instant-was]');
    const savingsEl = root.querySelector('[data-gd-instant-savings]');
    const atcBtn = root.querySelector('[data-gd-instant-atc]');
    const atcText = root.querySelector('[data-gd-instant-atc-text]');
    const errEl = root.querySelector('[data-gd-instant-error]');

    const selected = []; // [{ variantId, name, designId, image }]

    const fmt = (cents) => {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    };
    const emit = (name, detail) => {
      document.body.dispatchEvent(new CustomEvent(name, { detail }));
      if (window.dataLayer) window.dataLayer.push({ event: name, ...detail });
    };
    const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));

    // Modal open/close — Joel 2026-05-02 (scroll-lock audit fix):
    // migrated from raw body.style.overflow toggling to the shared
    // gdScrollLock manager so this overlay matches every other modal
    // on the site (cart drawer / header / search / stories /
    // lightbox / email modal / colours grid filter / kit builder).
    // Reference-counted, iOS-safe (position:fixed + savedScrollY
    // restore on final unlock). Defensive `else` keeps the prior
    // behaviour as a no-op fallback for the case where the asset
    // load order ever leaves gdScrollLock undefined.
    // wasOpen guard makes close() idempotent — duplicate dispatches
    // (ESC + click-out + close button on the same close gesture)
    // don't double-decrement the lock counter.
    const open = () => {
      if (!modal.hidden) return;
      modal.hidden = false;
      if (window.gdScrollLock) window.gdScrollLock.lock(modal);
      else document.body.style.overflow = 'hidden';
      emit('instant_gels_builder_open', {});
      setTimeout(() => modal.querySelector('[data-gd-instant-close]')?.focus(), 50);
    };
    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      if (window.gdScrollLock) window.gdScrollLock.unlock(modal);
      else document.body.style.overflow = '';
    };
    localOpen?.addEventListener('click', open);
    globalOpens.forEach(b => b.addEventListener('click', open));
    closeBtns.forEach(b => b.addEventListener('click', close));
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !modal.hidden) close(); });

    // Category filter
    catChips.forEach(chip => {
      chip.addEventListener('click', () => {
        const cat = chip.dataset.gdInstantCat;
        catChips.forEach(c => c.removeAttribute('data-active'));
        chip.setAttribute('data-active', 'true');
        designTiles.forEach(t => {
          t.hidden = cat !== '' && t.dataset.category !== cat;
        });
      });
    });

    // Active tier compute + render
    const activeTier = () => {
      if (tiers.length === 0) return null;
      let active = tiers[0];
      for (const t of tiers) if (t.count <= selected.length) active = t;
      return active;
    };
    const nextTier = () => {
      const cur = activeTier();
      if (!cur) return null;
      const idx = tiers.findIndex(t => t === cur);
      return idx >= 0 && idx < tiers.length - 1 ? tiers[idx + 1] : null;
    };

    const render = () => {
      const tier = activeTier();
      const nxt = nextTier();

      // Tier checkpoints: data-active on the current tier, is-reached on all tiers whose threshold is met by current selection count
      tiers.forEach(t => {
        t.el.setAttribute('aria-selected', t === tier ? 'true' : 'false');
        t.el.toggleAttribute('data-active', t === tier && selected.length > 0);
        t.el.classList.toggle('is-reached', selected.length >= t.count);
      });

      // Summary slots — show product thumbnail when filled, placeholder number otherwise
      if (slotsHost) {
        slotsHost.innerHTML = '';
        const slotCount = Math.max(minSelections, selected.length, tier?.count || 0);
        for (let i = 0; i < slotCount; i++) {
          const slot = document.createElement('div');
          slot.className = 'gd-instant__slot';
          if (selected[i]) {
            slot.setAttribute('data-filled', 'true');
            slot.title = selected[i].name;
            if (selected[i].image) {
              const img = document.createElement('img');
              img.src = selected[i].image;
              img.alt = selected[i].name;
              img.loading = 'lazy';
              slot.appendChild(img);
            } else {
              slot.textContent = selected[i].name.slice(0, 2);
            }
          } else {
            slot.textContent = String(i + 1);
          }
          slotsHost.appendChild(slot);
        }
      }

      // Price
      if (tier) {
        priceEl.textContent = fmt(tier.price);
        if (wasEl) {
          if (tier.compare && tier.compare > tier.price) {
            wasEl.textContent = fmt(tier.compare);
            wasEl.style.display = '';
          } else wasEl.style.display = 'none';
        }
      }

      // Savings / next-tier message
      if (savingsEl && tier) {
        if (nxt && selected.length > 0 && selected.length < nxt.count) {
          const nxtPct = nxt.compare ? Math.round(((nxt.compare - nxt.price) / nxt.compare) * 100) : null;
          const remaining = nxt.count - selected.length;
          savingsEl.textContent = nxtPct
            ? `Add ${remaining} more to save ${nxtPct}%`
            : `Add ${remaining} more to unlock the next tier`;
        } else if (!nxt && selected.length >= maxSelections) {
          const pct = tier.compare ? Math.round(((tier.compare - tier.price) / tier.compare) * 100) : null;
          savingsEl.textContent = pct ? `Max savings unlocked — saving ${pct}%` : 'Maximum bundle unlocked';
        } else {
          savingsEl.textContent = '';
        }
      }

      // Status + ATC
      if (statusEl) {
        if (selected.length === 0) statusEl.textContent = `Pick at least ${minSelections} designs.`;
        else if (selected.length < minSelections) statusEl.textContent = `Pick ${minSelections - selected.length} more.`;
        else statusEl.textContent = `${selected.length} selected. Ready when you are.`;
      }
      const ready = selected.length >= minSelections;
      if (atcBtn) atcBtn.disabled = !ready;
      if (atcText) atcText.textContent = ready ? 'Add bundle to cart' : 'Pick designs to continue';
    };

    // Joel 2026-05-02 (OOS pre-validation Fix B+C):
    // See kit-builder.js preValidateTile docs for the shared-SKU rationale.
    // Same dual-fetch: query duplicate AND paired regular product (handle
    // minus -bundle suffix). Regular's `available` is the only reliable
    // signal because the shared-SKU pool's inventory tracking lives there.
    async function preValidateTile(tile) {
      const handle = tile.dataset.productHandle;
      const variantId = String(tile.dataset.variantId || '');
      if (!handle || !variantId) return true;

      const regularHandle = handle.endsWith('-bundle')
        ? handle.slice(0, -'-bundle'.length)
        : handle;

      const fetchProduct = async (h) => {
        try {
          const resp = await fetch(`/products/${h}.js?_=${Date.now()}`, {
            credentials: 'same-origin',
            headers: { 'Cache-Control': 'no-cache', Accept: 'application/json' },
          });
          if (!resp.ok) return null;
          return await resp.json();
        } catch (e) {
          return null;
        }
      };

      try {
        const [duplicate, regular] = await Promise.all([
          fetchProduct(handle),
          regularHandle !== handle ? fetchProduct(regularHandle) : Promise.resolve(null),
        ]);

        if (duplicate) {
          const dupVariant = (duplicate.variants || []).find((v) => String(v.id) === variantId);
          if (dupVariant && dupVariant.available === false) return false;
        }

        if (regular && regular.available === false) {
          console.info('[gd-instant-gels-builder] OOS via paired regular:', regularHandle);
          return false;
        }

        if (regular && duplicate) {
          const dupVariant = (duplicate.variants || []).find((v) => String(v.id) === variantId);
          const dupSku = dupVariant && dupVariant.sku;
          if (dupSku) {
            const matchingRegular = (regular.variants || []).find((v) => v.sku === dupSku);
            if (matchingRegular && matchingRegular.available === false) {
              console.info('[gd-instant-gels-builder] OOS via shared-SKU regular variant:', dupSku);
              return false;
            }
          }
        }

        return true;
      } catch (e) {
        console.warn('[gd-instant-gels-builder] OOS pre-check failed, deferring to cart-add:', e && e.message);
        return true;
      }
    }

    // Joel 2026-05-02 (optimistic-with-undo): mirrors kit-builder.js. ADD
    // pushes to selection immediately + renders; preValidateTile fires in
    // background; if it resolves false, the selection is silently undone,
    // tile marked OOS, transient toast surfaces via [data-gd-instant-error].
    function showOosNotice(name) {
      if (!errEl) return;
      errEl.hidden = false;
      errEl.textContent = `${name || 'That design'} just sold out — pick another.`;
      errEl.classList.add('gd-instant__error--oos');
      if (errEl._oosTimer) clearTimeout(errEl._oosTimer);
      errEl._oosTimer = setTimeout(() => {
        errEl.hidden = true;
        errEl.textContent = '';
        errEl.classList.remove('gd-instant__error--oos');
        errEl._oosTimer = null;
      }, 4000);
    }

    // Design toggle
    designTiles.forEach(tile => {
      const cb = tile.querySelector('input[type="checkbox"]');
      tile.addEventListener('click', (e) => {
        if (e.target !== cb) e.preventDefault();
        const variantId = parseInt(tile.dataset.variantId, 10);
        const name = tile.dataset.name;
        const designId = tile.dataset.designId;
        const image = tile.dataset.image || '';
        const existing = selected.findIndex(d => d.variantId === variantId);
        const prevTier = activeTier();

        if (existing >= 0) {
          // Removing — no OOS check needed.
          selected.splice(existing, 1);
          cb.checked = false;
          tile.removeAttribute('data-selected');
          emit('instant_gels_design_toggle', { design_id: designId, variant_id: variantId, action: 'remove' });
        } else {
          // Tier cap (synchronous) before optimistic push.
          if (selected.length >= maxSelections) {
            tile.classList.add('is-shake');
            setTimeout(() => tile.classList.remove('is-shake'), 400);
            cb.checked = false;
            return;
          }
          // Optimistic add.
          selected.push({ variantId, name, designId, image });
          cb.checked = true;
          tile.setAttribute('data-selected', 'true');
          emit('instant_gels_design_toggle', { design_id: designId, variant_id: variantId, action: 'add' });

          // Background validation. If OOS, silently undo.
          preValidateTile(tile).then((ok) => {
            if (ok) return;
            const idx = selected.findIndex((d) => d.variantId === variantId);
            if (idx >= 0) selected.splice(idx, 1);
            cb.checked = false;
            tile.removeAttribute('data-selected');
            tile.classList.add('gd-instant__design--oos');
            tile.setAttribute('aria-disabled', 'true');
            emit('instant_gels_design_oos_blocked', { design_id: designId, variant_id: variantId });
            showOosNotice(name);
            render();
          });
        }

        const nextT = activeTier();
        if (prevTier?.variantId !== nextT?.variantId) {
          emit('instant_gels_tier_changed', { from_tier: prevTier?.variantId, to_tier: nextT?.variantId, to_count: nextT?.count });
        }
        render();
      });
    });

    // ATC
    const handleAtc = async () => {
      const tier = activeTier();
      if (!tier || selected.length < minSelections) return;
      atcBtn.disabled = true;
      if (atcText) atcText.textContent = 'Adding...';
      errEl.hidden = true;
      const bundleId = uuid();
      const designsStr = selected.map(d => d.name).join(',');
      const items = [
        { id: tier.variantId, quantity: 1, properties: { Designs: designsStr, __bundle_id: bundleId } },
        ...selected.map(d => ({
          id: d.variantId,
          quantity: 1,
          properties: { __bundle_id: bundleId, _hidden: 'true' },
        })),
      ];
      emit('instant_gels_atc_click', { tier_id: tier.variantId, design_count: selected.length, price: tier.price });
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.description || 'Cart add failed');
        }
        emit('instant_gels_atc_success', { tier_id: tier.variantId });
        // Klaviyo "Added to Cart" — bundle-level. tier.price is cents
        // (sourced from variant.price via data-price attr in the section).
        try {
          if (window._learnq && typeof window._learnq.push === 'function') {
            const tierPriceDollars = (tier.price || 0) / 100;
            window._learnq.push(['track', 'Added to Cart', {
              $value: tierPriceDollars,
              AddedItemProductName: tier.title || 'Instant Gels Bundle',
              AddedItemProductID:   tier.variantId ? String(tier.variantId) : '',
              AddedItemQuantity:    1,
              AddedItemPrice:       tierPriceDollars,
              AddedItemURL:         window.location.origin + window.location.pathname,
              AddedItemImageURL:    tier.image || (tier.featured_image && tier.featured_image.url) || '',
              AddedItemCategories:  ['Instant Gels'],
              BundleTier:           tier.title || '',
              BundleDesigns:        selected.map(d => d.name).filter(Boolean),
              BundleDesignCount:    selected.length,
            }]);
          }
        } catch (e) {}
        document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
        document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
        close();
        // Reset selections
        selected.length = 0;
        designTiles.forEach(t => { t.removeAttribute('data-selected'); const cb = t.querySelector('input[type="checkbox"]'); if (cb) cb.checked = false; });
        render();
      } catch (e) {
        emit('instant_gels_atc_error', { message: e.message });
        if (errEl) { errEl.textContent = e.message; errEl.hidden = false; }
        if (atcText) atcText.textContent = 'Add bundle to cart';
        atcBtn.disabled = false;
      }
    };
    atcBtn?.addEventListener('click', handleAtc);

    render();
  }
})();
